import { useEffect, useMemo, useState } from 'react';
import {
    Background,
    Controls,
    Handle,
    MarkerType,
    MiniMap,
    Position,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import processFlowGraph from './processFlowGraph';

const { applyLayoutPositions, buildFlowElements, toElkGraph } = processFlowGraph;

const elk = new ELK();

function statusLabel(status) {
    if (typeof status !== 'number') return 'Unknown';
    const work =
        {
            6: '',
            7: 'Rework - ',
            8: 'Check Rework - ',
        }[Math.floor(status / 100)] ?? 'Unknown Work - ';
    const text =
        {
            0: 'Auto Assign',
            1: 'Assigned',
            2: 'Selected',
            3: 'Loaded',
            4: 'In-Progress',
            5: 'Mark For Complete',
            6: 'Complete',
            7: 'Manually Completed',
            8: 'Rejected',
        }[status % 10] ?? 'Unknown';
    return `${work}${text}`;
}

function StageNode({ data }) {
    const highlight = data.highlight || 'idle';
    return (
        <div className={`reports-rf-node is-${highlight}`} title="Click to list SKUs">
            <Handle type="target" position={Position.Left} />
            <div className="reports-rf-name">{data.stageName}</div>
            <div className="reports-rf-split">
                <button
                    type="button"
                    className="reports-rf-chip is-pending"
                    onClick={event => {
                        event.stopPropagation();
                        data.onOpen?.();
                    }}
                    onMouseDown={event => event.stopPropagation()}
                >
                    Pending SKUs <strong>{(data.pendingCount || 0).toLocaleString()}</strong>
                </button>
            </div>
            <div className="reports-rf-counts">
                <strong>{(data.fileCount || 0).toLocaleString()}</strong> files here
            </div>
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

function DeadendNode({ data }) {
    return (
        <div className={`reports-rf-deadend is-${data.highlight || 'idle'}`}>
            <Handle type="target" position={Position.Left} />
            X
        </div>
    );
}

const nodeTypes = { stage: StageNode, deadend: DeadendNode };

async function layoutGraph(baseGraph) {
    if (!baseGraph.nodes.length) return baseGraph;
    const layouted = await elk.layout(toElkGraph(baseGraph));
    return applyLayoutPositions(baseGraph, layouted.children);
}

function FlowCanvas({ processFlow, stageCounts, onStageClick }) {
    const { fitView } = useReactFlow();
    const countByCpId = useMemo(
        () => new Map((stageCounts || []).map(item => [item.cpId, item])),
        [stageCounts]
    );
    const baseGraph = useMemo(() => {
        const { nodes, edges } = buildFlowElements(processFlow, stageCounts);
        return {
            nodes: nodes.map(node => {
                if (node.type !== 'stage') return node;
                const counts = countByCpId.get(node.data.cpId) || {};
                return {
                    ...node,
                    data: {
                        ...node.data,
                        onOpen: () => {
                            if (!onStageClick) return;
                            onStageClick({
                                cpId: node.data.cpId,
                                stageName: `${node.data.stageName} · Pending`,
                                skuIds: counts.pendingSkuIds || counts.skuIds || [],
                            });
                        },
                    },
                };
            }),
            edges: edges.map(edge => ({
                ...edge,
                markerEnd: { type: MarkerType.ArrowClosed, color: edge.style.stroke },
            })),
        };
    }, [processFlow, stageCounts, countByCpId, onStageClick]);

    const [graph, setGraph] = useState({ nodes: [], edges: [] });

    useEffect(() => {
        let cancelled = false;
        layoutGraph(baseGraph).then(result => {
            if (!cancelled) setGraph(result);
        });
        return () => {
            cancelled = true;
        };
    }, [baseGraph]);

    useEffect(() => {
        if (!graph.nodes.length) return undefined;
        const frame = requestAnimationFrame(() => fitView({ padding: 0.18, duration: 200 }));
        return () => cancelAnimationFrame(frame);
    }, [graph, fitView]);

    return (
        <ReactFlow
            nodes={graph.nodes}
            edges={graph.edges}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag
            zoomOnScroll
            minZoom={0.15}
            maxZoom={1.6}
            fitView
            fitViewOptions={{ padding: 0.18 }}
            attributionPosition="bottom-left"
            defaultEdgeOptions={{ type: 'default' }}
            onNodeClick={(_, node) => {
                if (node.type !== 'stage' || !onStageClick) return;
                const counts = countByCpId.get(node.data.cpId) || {};
                onStageClick({
                    cpId: node.data.cpId,
                    stageName: `${node.data.stageName} · Pending`,
                    skuIds: counts.pendingSkuIds || counts.skuIds || [],
                });
            }}
        >
            <Background color="#f0f0f0" gap={16} size={1} />
            <Controls
                showInteractive={false}
                style={{ backgroundColor: '#fff', border: '1px solid #d9d9d9' }}
            />
            <MiniMap
                pannable
                zoomable
                nodeColor={node =>
                    node.data?.highlight === 'pending' ? '#d48806' : node.data?.highlight === 'done' ? '#52c41a' : '#d0d5dd'
                }
                style={{ backgroundColor: '#fff' }}
            />
        </ReactFlow>
    );
}

export default function ProcessFlowDiagram({ processFlow, stageCounts, onStageClick }) {
    if (!processFlow?.length) {
        return <p className="reports-flow-empty">No process flow on this config.</p>;
    }

    return (
        <div className="reports-flow-canvas">
            <ReactFlowProvider>
                <FlowCanvas processFlow={processFlow} stageCounts={stageCounts} onStageClick={onStageClick} />
            </ReactFlowProvider>
        </div>
    );
}

export { statusLabel };
